import {
  LayoutDashboard, Wallet, BarChart3, Repeat, Bot, Rss, Terminal, Clock,
  CalendarClock, Grid3x3, Building2, Users, ReceiptText, Settings, BrainCircuit, CreditCard,
} from "lucide-react";

// Config-driven navigation. `functional: true` items route to real pages;
// everything else renders the Coming Soon module page with its planned sections.

export const adminNav = [
  {
    group: null,
    items: [
      { label: "Command Center", path: "/dashboard", icon: LayoutDashboard, functional: true },
      { label: "My Exchanges", path: "/exchanges", icon: Wallet, functional: true },
      { label: "Portfolio Tracker", path: "/portfolio", icon: BarChart3, functional: true },
    ],
  },
  {
    group: "Services",
    items: [
      {
        label: "Copy Trading", path: "/copy-trading", icon: Repeat, category: "Services",
        description: "Mirror a master's trades across unlimited copy accounts with per-copier risk controls.",
        sections: [
          { title: "Copy Groups", desc: "Create and manage master → copier groups." },
          { title: "Copiers Management", desc: "Add, pause or remove copiers per master." },
          { title: "Risk & Allocation", desc: "Fixed amount, proportional or multiplier sizing." },
          { title: "Copy Performance", desc: "Track slippage, fill rate and copier PNL." },
        ],
      },
      {
        label: "TradingView Bot", path: "/tradingview-bot", icon: Bot, category: "Services",
        description: "Turn TradingView alerts into live orders via secure webhooks.",
        sections: [
          { title: "Webhook Endpoints", desc: "Generate and rotate signed webhook URLs." },
          { title: "Alert Mapping", desc: "Map alert payloads to symbols, side and size." },
          { title: "Bot Rules", desc: "Guardrails: max size, allowed symbols, cooldowns." },
          { title: "Execution Logs", desc: "Every alert → order with status and latency." },
        ],
      },
      {
        label: "Signal Bot", path: "/signal-bot", icon: Rss, category: "Services",
        description: "Subscribe to signal providers and auto-execute their calls.",
        sections: [
          { title: "Signal Sources", desc: "Connect Telegram / Discord signal channels." },
          { title: "Subscriptions", desc: "Manage active provider subscriptions." },
          { title: "Auto-Execution", desc: "Rules for entries, TP/SL and position sizing." },
          { title: "Signal History", desc: "Audit of parsed signals and outcomes." },
        ],
      },
      {
        label: "Terminal", path: "/terminal", icon: Terminal, category: "Services",
        description: "A fast manual trading terminal across all connected accounts.",
        sections: [
          { title: "Order Ticket", desc: "Market, limit and reduce-only orders." },
          { title: "Positions & Orders", desc: "Live positions with one-click close." },
          { title: "Multi-Account", desc: "Route the same order to selected accounts." },
          { title: "Hotkeys", desc: "Keyboard-driven execution for speed." },
        ],
      },
    ],
  },
  {
    group: "Advanced Executions",
    items: [
      {
        label: "TWAP", path: "/twap", icon: Clock, category: "Advanced Executions",
        description: "Time-Weighted Average Price execution to minimise market impact.",
        sections: [
          { title: "Schedule TWAP", desc: "Set total size, duration and interval." },
          { title: "Slice Configuration", desc: "Randomised slice sizing and timing." },
          { title: "Active Orders", desc: "Monitor and cancel running TWAPs." },
          { title: "Execution Report", desc: "Achieved vs benchmark price analysis." },
        ],
      },
      {
        label: "DCA", path: "/dca", icon: CalendarClock, category: "Advanced Executions",
        badge: { label: "new", tone: "gold" },
        description: "Dollar-Cost Averaging plans with recurring, rule-based buys.",
        sections: [
          { title: "DCA Plans", desc: "Create recurring accumulation plans." },
          { title: "Trigger Conditions", desc: "Time-based or dip-based triggers." },
          { title: "Plan Analytics", desc: "Average entry, invested and PNL." },
          { title: "Auto-Rebalance", desc: "Keep allocations on target over time." },
        ],
      },
      {
        label: "Grid", path: "/grid", icon: Grid3x3, category: "Advanced Executions",
        badge: { label: "soon", tone: "muted" },
        description: "Automated grid trading that profits from range-bound volatility.",
        sections: [
          { title: "Grid Setup", desc: "Define range, levels and per-grid size." },
          { title: "Range & Levels", desc: "Arithmetic or geometric grid spacing." },
          { title: "Active Grids", desc: "Live grid status and realised profit." },
          { title: "Grid Profit", desc: "Per-level fills and cumulative earnings." },
        ],
      },
    ],
  },
  {
    group: "White Label",
    items: [
      {
        label: "Overview", path: "/white-label/overview", icon: Building2, category: "White Label",
        description: "Your branded platform's health at a glance.",
        sections: [
          { title: "Brand Metrics", desc: "Active users, AUM and volume." },
          { title: "Client Growth", desc: "Sign-ups and retention trends." },
          { title: "Revenue Snapshot", desc: "Commission and subscription revenue." },
          { title: "Activity Feed", desc: "Recent platform-wide events." },
        ],
      },
      {
        label: "Users", path: "/white-label/users", icon: Users, category: "White Label",
        description: "Manage the users of your white-label platform.",
        sections: [
          { title: "User Directory", desc: "Search and inspect all users." },
          { title: "Roles & Permissions", desc: "Granular role-based access control." },
          { title: "Invitations", desc: "Invite partners and staff." },
          { title: "Access Logs", desc: "Sign-in and action audit trail." },
        ],
      },
      {
        label: "Accounting", path: "/white-label/accounting", icon: ReceiptText, category: "White Label",
        description: "Invoices, commissions and payouts across your platform.",
        sections: [
          { title: "Invoices", desc: "Issue and track client invoices." },
          { title: "Commissions", desc: "Configure and compute commission tiers." },
          { title: "Payouts", desc: "Schedule and reconcile partner payouts." },
          { title: "Statements", desc: "Exportable monthly statements." },
        ],
      },
      {
        label: "Settings", path: "/settings", icon: Settings, category: "White Label",
        description: "Configure branding, domains and platform-wide settings.",
        sections: [
          { title: "Branding", desc: "Logo, colours and email templates." },
          { title: "Domains", desc: "Attach and verify custom domains." },
          { title: "API Keys", desc: "Platform API credentials and scopes." },
          { title: "Security", desc: "2FA, session and IP policies." },
        ],
      },
    ],
  },
];

export const partnerNav = [
  {
    group: null,
    items: [
      {
        label: "My Clients", path: "/partner/clients", icon: Users, functional: true, category: "Clients",
      },
      {
        label: "Portfolio Tracker", path: "/partner/portfolio", icon: BarChart3, functional: true, category: "Portfolio",
      },
    ],
  },
  {
    group: "Services",
    items: [
      {
        label: "AI Strategy", path: "/partner/ai-strategy", icon: BrainCircuit, category: "Services",
        description: "Deploy and manage AI-driven strategies for your clients.",
        sections: [
          { title: "Strategy Marketplace", desc: "Browse available AI strategies." },
          { title: "Deploy Strategy", desc: "Assign strategies to client accounts." },
          { title: "Risk Controls", desc: "Per-strategy limits and safeguards." },
          { title: "Performance", desc: "Live strategy performance tracking." },
        ],
      },
      {
        label: "Billing", path: "/partner/billing", icon: CreditCard, category: "Services",
        description: "Subscriptions, commissions and payouts for your partnership.",
        sections: [
          { title: "Plans & Subscriptions", desc: "Your active plan and usage." },
          { title: "Commission Payouts", desc: "Track earnings and payout history." },
          { title: "Invoices", desc: "Download billing invoices." },
          { title: "Payment Methods", desc: "Manage cards and payout accounts." },
        ],
      },
    ],
  },
];

export const flattenNav = (nav) => nav.flatMap((g) => g.items);
export const comingSoonItems = (nav) => flattenNav(nav).filter((i) => !i.functional);
export const findModule = (nav, path) => flattenNav(nav).find((i) => i.path === path);
